/* ============================================================================
   <custom-header> — desktop mega menu + mobile drawer
   ============================================================================ */
(function () {
  'use strict';

  if (customElements.get('custom-header')) return;

  const HOVER_OPEN_DELAY = 80;
  const HOVER_CLOSE_DELAY = 180;

  class CustomHeader extends HTMLElement {
    constructor() {
      super();
      this.openHandle = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.activeMobilePath = []; // ['products'] or ['products', 'products::4']
    }

    connectedCallback() {
      this.wrapper = this.closest('.ch-header-wrapper') || this;
      this.megaRoot = this.querySelector('[data-ch-mega-root]');
      this.navItems = this.querySelectorAll('[data-ch-nav-item]');
      this.overlay = this.wrapper.querySelector('[data-ch-overlay]');
      this.drawer = document.querySelector('[data-ch-mdrawer]');
      this.burger = this.querySelector('[data-ch-burger]');

      this.bindDesktop();
      this.bindMobile();

      window.addEventListener('keydown', this.onKeyDown.bind(this));
      document.addEventListener('click', this.onDocumentClick.bind(this), true);
      window.matchMedia('(min-width: 990px)').addEventListener('change', () => this.closeAll());
    }

    /* ------- Desktop hover/click -------------------------------------- */
    bindDesktop() {
      this.navItems.forEach((item) => {
        const handle = item.dataset.chMegaTarget;
        const trigger = item.querySelector('[data-ch-nav-trigger]');
        if (!trigger) return;

        item.addEventListener('mouseenter', () => this.scheduleOpen(handle));
        item.addEventListener('mouseleave', () => this.scheduleClose());

        trigger.addEventListener('focus', () => this.openMega(handle));
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.openHandle === handle ? this.closeMega() : this.openMega(handle);
        });
      });

      // each panel keeps menu open on hover, closes on leave
      this.megaRoot && this.megaRoot.querySelectorAll('[data-ch-mega-panel]').forEach((panel) => {
        panel.addEventListener('mouseenter', () => this.cancelClose());
        panel.addEventListener('mouseleave', () => this.scheduleClose());
        this.bindPills(panel);
      });

      if (this.overlay) {
        this.overlay.addEventListener('mouseenter', () => this.scheduleClose());
        this.overlay.addEventListener('click', () => this.closeMega());
      }
    }

    bindPills(panel) {
      const pills = panel.querySelectorAll('[data-ch-pill]');
      const contents = panel.querySelectorAll('[data-ch-content]');

      pills.forEach((pill) => {
        pill.addEventListener('mouseenter', () => this.activatePill(panel, pill));
        pill.addEventListener('focus', () => this.activatePill(panel, pill));
        pill.addEventListener('click', (e) => {
          this.activatePill(panel, pill);
          const link = pill.dataset.chPillLink;
          if (link) {
            e.preventDefault();
            window.location.href = link;
          }
        });
      });

      // ensure default tabindex/aria sync
      pills.forEach((p) => p.setAttribute('tabindex', p.classList.contains('ch-mega__pill--active') ? '0' : '-1'));
      contents.forEach((c) => c.toggleAttribute('hidden', !c.classList.contains('ch-mega__content--active')));
    }

    activatePill(panel, pill) {
      const idx = pill.dataset.chPill;
      panel.querySelectorAll('[data-ch-pill]').forEach((p) => {
        const active = p === pill;
        p.classList.toggle('ch-mega__pill--active', active);
        p.setAttribute('aria-selected', active ? 'true' : 'false');
        p.setAttribute('tabindex', active ? '0' : '-1');
      });
      panel.querySelectorAll('[data-ch-content]').forEach((c) => {
        const active = c.dataset.chContent === idx;
        c.classList.toggle('ch-mega__content--active', active);
        c.toggleAttribute('hidden', !active);
      });
    }

    scheduleOpen(handle) {
      this.cancelClose();
      clearTimeout(this.openTimer);
      this.openTimer = setTimeout(() => this.openMega(handle), HOVER_OPEN_DELAY);
    }

    scheduleClose() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closeMega(), HOVER_CLOSE_DELAY);
    }

    cancelClose() {
      clearTimeout(this.closeTimer);
    }

    openMega(handle) {
      if (window.matchMedia('(max-width: 989px)').matches) return;
      if (!handle) return;

      this.openHandle = handle;
      this.wrapper.classList.add('ch-mega-open');

      this.navItems.forEach((item) => {
        const isMatch = item.dataset.chMegaTarget === handle;
        item.dataset.chMegaActive = isMatch ? 'true' : 'false';
        const trigger = item.querySelector('[data-ch-nav-trigger]');
        trigger && trigger.setAttribute('aria-expanded', isMatch ? 'true' : 'false');
      });

      this.megaRoot.querySelectorAll('[data-ch-mega-panel]').forEach((panel) => {
        const isMatch = panel.dataset.chMegaPanel === handle;
        panel.dataset.chActive = isMatch ? 'true' : 'false';
        panel.toggleAttribute('hidden', !isMatch);
      });
      this.megaRoot.setAttribute('aria-hidden', 'false');
    }

    closeMega() {
      this.openHandle = null;
      this.wrapper.classList.remove('ch-mega-open');
      this.navItems.forEach((item) => {
        item.dataset.chMegaActive = 'false';
        const t = item.querySelector('[data-ch-nav-trigger]');
        t && t.setAttribute('aria-expanded', 'false');
      });
      if (this.megaRoot) {
        this.megaRoot.querySelectorAll('[data-ch-mega-panel]').forEach((p) => {
          p.dataset.chActive = 'false';
          p.setAttribute('hidden', '');
        });
        this.megaRoot.setAttribute('aria-hidden', 'true');
      }
    }

    /* ------- Mobile drawer -------------------------------------------- */
    bindMobile() {
      if (!this.drawer || !this.burger) return;

      this.burger.addEventListener('click', () => this.openDrawer());
      this.drawer.querySelectorAll('[data-ch-mdrawer-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeDrawer());
      });

      this.drawer.querySelectorAll('[data-ch-mdrawer-open-level2]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.pushMobileLevel(2, btn.dataset.target, btn.dataset.title);
        });
      });

      this.drawer.querySelectorAll('[data-ch-mdrawer-open-level3]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.pushMobileLevel(3, btn.dataset.target, btn.dataset.title);
        });
      });

      const backBtn = this.drawer.querySelector('[data-ch-mdrawer-back]');
      backBtn && backBtn.addEventListener('click', () => this.popMobileLevel());
    }

    openDrawer() {
      this.drawer.removeAttribute('hidden');
      this.drawer.setAttribute('aria-hidden', 'false');
      // reflow to enable transition
      requestAnimationFrame(() => {
        this.drawer.dataset.open = 'true';
        document.body.classList.add('ch-no-scroll');
      });
      this.burger.setAttribute('aria-expanded', 'true');
      this.activeMobilePath = [];
      this.renderMobileLevels();
    }

    closeDrawer() {
      this.drawer.dataset.open = 'false';
      document.body.classList.remove('ch-no-scroll');
      this.burger.setAttribute('aria-expanded', 'false');
      // wait for transition then hide
      setTimeout(() => {
        this.drawer.setAttribute('aria-hidden', 'true');
        this.drawer.setAttribute('hidden', '');
        this.activeMobilePath = [];
        this.renderMobileLevels();
      }, 320);
    }

    pushMobileLevel(level, key, title) {
      if (level === 2) this.activeMobilePath = [key];
      else if (level === 3) {
        // ensure parent (level-2 key) is preserved
        const parent = key.split('::')[0];
        if (this.activeMobilePath[0] !== parent) this.activeMobilePath[0] = parent;
        this.activeMobilePath[1] = key;
      }
      this.renderMobileLevels(title);
    }

    popMobileLevel() {
      if (this.activeMobilePath.length === 0) {
        this.closeDrawer();
        return;
      }
      this.activeMobilePath.pop();
      this.renderMobileLevels();
    }

    renderMobileLevels(titleOverride) {
      const depth = this.activeMobilePath.length; // 0 -> level1, 1 -> level2, 2 -> level3
      const titleEl = this.drawer.querySelector('[data-ch-mdrawer-title]');
      const backBtn = this.drawer.querySelector('[data-ch-mdrawer-back]');
      const backText = this.drawer.querySelector('[data-ch-mdrawer-back-text]');

      // toggle level visibility
      this.drawer.querySelectorAll('.ch-mdrawer__level').forEach((lvl) => {
        const lvlNum = parseInt(lvl.dataset.level, 10);
        const key = lvl.dataset.key;
        let active = false;
        let pushed = false;
        if (lvlNum === 1) {
          active = depth === 0;
          pushed = depth > 0;
        } else if (lvlNum === 2) {
          active = depth === 1 && key === this.activeMobilePath[0];
          pushed = depth === 2 && key === this.activeMobilePath[0];
        } else if (lvlNum === 3) {
          active = depth === 2 && key === this.activeMobilePath[1];
        }
        lvl.dataset.active = active ? 'true' : 'false';
        lvl.dataset.pushed = pushed ? 'true' : 'false';
        if (lvlNum === 1) {
          // level 1 always slides; reset its transform when active
          lvl.style.transform = active ? 'translateX(0)' : 'translateX(-30%)';
          lvl.style.visibility = 'visible';
        } else {
          // levels 2/3 use right-side stack
          lvl.style.transform = active ? 'translateX(0)' : (pushed ? 'translateX(-30%)' : 'translateX(100%)');
          lvl.style.visibility = (active || pushed) ? 'visible' : 'hidden';
        }
      });

      // back button
      if (depth === 0) {
        backBtn.setAttribute('hidden', '');
        titleEl.textContent = 'Menu';
      } else {
        backBtn.removeAttribute('hidden');
        if (titleOverride) {
          titleEl.textContent = titleOverride;
          backText.textContent = 'Back';
        } else if (depth === 1) {
          // re-derive title from current button (best effort)
          const btn = this.drawer.querySelector('[data-ch-mdrawer-open-level2][data-target="' + this.activeMobilePath[0] + '"]');
          titleEl.textContent = btn ? btn.dataset.title : '';
        } else if (depth === 2) {
          const btn = this.drawer.querySelector('[data-ch-mdrawer-open-level3][data-target="' + this.activeMobilePath[1] + '"]');
          titleEl.textContent = btn ? btn.dataset.title : '';
        }
      }
    }

    /* ------- Misc ---------------------------------------------------- */
    onKeyDown(e) {
      if (e.key !== 'Escape') return;
      if (this.openHandle) this.closeMega();
      if (this.drawer && this.drawer.dataset.open === 'true') this.closeDrawer();
    }

    onDocumentClick(e) {
      if (!this.openHandle) return;
      if (this.contains(e.target)) return;
      if (this.megaRoot && this.megaRoot.contains(e.target)) return;
      this.closeMega();
    }

    closeAll() {
      this.closeMega();
      if (this.drawer && this.drawer.dataset.open === 'true') this.closeDrawer();
    }
  }

  customElements.define('custom-header', CustomHeader);

  /* --- Cart subtotal live update ----------------------------------------
     Dawn replaces #cart-icon-bubble.innerHTML when cart updates. We observe
     that element and refetch /cart.js to refresh our subtotal text.        */
  function bindCartSubtotalSync() {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    const observer = new MutationObserver(refreshCartSubtotal);
    observer.observe(bubble, { childList: true, subtree: true, characterData: true });
  }

  function refreshCartSubtotal() {
    const targets = document.querySelectorAll('[data-ch-cart-total]');
    if (targets.length === 0) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        targets.forEach((el) => { el.textContent = formatMoney(cart.total_price); });
      })
      .catch(() => {});
  }

  function formatMoney(cents) {
    if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents);
    return '$' + (cents / 100).toFixed(2);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindCartSubtotalSync, { once: true });
  } else {
    bindCartSubtotalSync();
  }

  /* --- Transparent header: toggle .ch-is-scrolled past threshold ---------- */
  function bindTransparentScroll() {
    const wrapper = document.querySelector('.ch-header-wrapper--transparent');
    if (!wrapper) return;
    document.body.classList.add('ch-has-transparent-header');

    // Read threshold from CSS var (set inline by Liquid). Fallback to 30px.
    const cssThreshold = getComputedStyle(wrapper).getPropertyValue('--ch-transparent-scroll-threshold').trim();
    const threshold = parseInt(cssThreshold, 10) || 30;

    let lastState = null;
    const update = () => {
      const scrolled = window.scrollY > threshold;
      if (scrolled !== lastState) {
        wrapper.classList.toggle('ch-is-scrolled', scrolled);
        lastState = scrolled;
      }
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindTransparentScroll, { once: true });
  } else {
    bindTransparentScroll();
  }

  /* --- Language picker click-outside + escape close --------------------
     Native <details> stays open until the <summary> is clicked again. We
     add a global click-outside + Escape handler so it behaves like a real
     menu dropdown. Submitting a language closes the dropdown by definition
     because the page navigates. */
  function bindLangPicker() {
    const all = () => document.querySelectorAll('details[data-ch-lang-details][open]');

    document.addEventListener('click', (e) => {
      all().forEach((details) => {
        if (!details.contains(e.target)) details.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      all().forEach((details) => {
        details.removeAttribute('open');
        const summary = details.querySelector('summary');
        summary && summary.focus();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindLangPicker, { once: true });
  } else {
    bindLangPicker();
  }
})();
